import React, { useState, useRef, useEffect } from "react";
import ProjectLanguages from "../projectLanguages/ProjectLanguages";
import ProjectLinks from "../ProjectLinks/ProjectLinks";
import "./ProjectCard.css";
import { Fade } from "react-reveal";
import { style } from "glamor";
import { trackProjectClick } from "../analytics/Analytics";

export default function ProjectCard({ repo, theme }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const descriptionRef = useRef(null);

  // Check if text is actually truncated in the UI
  useEffect(() => {
    const checkTruncation = () => {
      if (descriptionRef.current) {
        const element = descriptionRef.current;
        setIsTruncated(element.scrollHeight > element.clientHeight);
      }
    };

    checkTruncation();
    // Recheck on window resize
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [repo.description]);

  const styles = style({
    backgroundColor: theme.projectCard,
    boxShadow: `rgba(0, 0, 0, 0.1) 0px 5px 15px -5px`,
    padding: "0",
    height: isExpanded ? "auto" : "100%",
    transition: "all 0.3s ease-in-out",
    ":hover": {
      boxShadow: `${theme.imageDark} 0 5px 20px`,
    },
  });

  // Truncate description if too long
  const truncateDescription = (description, limit = 120) => {
    if (!description) return "";
    if (isExpanded) return description;
    return description;
  };

  // Get a gradient background based on the repo name
  const getCardAccent = (name) => {
    // Generate a somewhat unique hue based on the repo name
    const hue =
      name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) % 360;
    return {
      background: `linear-gradient(135deg, hsla(${hue}, 70%, 90%, 0.1) 0%, hsla(${hue}, 70%, 90%, 0) 100%)`,
      borderTop: `3px solid hsla(${hue}, 70%, 60%, 0.7)`,
    };
  };

  const cardAccentStyle = getCardAccent(repo.name);

  // Handle project card click
  const handleProjectClick = () => {
    trackProjectClick(repo.name);
  };

  return (
    <Fade bottom duration={2000} distance="40px">
      <div
        className="repo-card"
        {...styles}
        style={{
          ...styles,
          ...cardAccentStyle,
        }}
        onClick={handleProjectClick}
      >
        <div className="repo-card-inner">
          <div className="repo-name-div">
            <h3 className="repo-name" style={{ color: theme.text }}>
              {repo.name}
            </h3>
          </div>

          <div className="repo-description-container">
            <p
              ref={descriptionRef}
              className={`repo-description ${isExpanded ? "expanded" : ""}`}
              style={{ color: theme.secondaryText }}
            >
              {truncateDescription(repo.description)}
            </p>

            {isTruncated && (
              <button
                className={`read-more-button ${isExpanded ? "expanded" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                style={{
                  color: theme.highlight,
                  marginTop: "8px",
                }}
              >
                {isExpanded ? "Show Less" : "Read More"}
              </button>
            )}
          </div>

          <div className="flexDiv">
            <div className="Leftitem">
              <ProjectLanguages logos={repo.languages} />
            </div>
            <div className="Rightitem">
              <ProjectLinks logos={repo.links} />
            </div>
          </div>
        </div>
      </div>
    </Fade>
  );
}
